import React, { useState, useEffect, useRef } from "react";
import { createConsumer } from "@rails/actioncable";
import { Send as SendIcon, Info as InfoIcon } from "lucide-react";
import AppLayout from "../Layout/AppLayout";

function formatTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (_e) {
    return "";
  }
}

// Shared 1:1 messages page used by both seller and partner routes. Reads
// from the unified my_conversations payload — each conversation row brings
// its own role-aware `context` block (label + link) keyed off
// conversation_type. The page itself is role-agnostic; what differs per
// viewer is which conversations they see and how each is labeled.
export default function MessagesPage({
  conversations = [],
  loading = false,
  persona = "SELLER",
  userName,
  userInitials,
  planProgress,
  actionPlanBadge,
  isPartner = false,
  breadcrumb = "MESSAGES",
  onSendReply,
  onIncomingMessage,
  sending = false,
}) {
  const leadIdFromUrl = new URLSearchParams(window.location.search).get("lead");

  // Hold only the id in state and derive the live conversation object from
  // the conversations prop on every render. Storing the object directly
  // would freeze the message list to the snapshot at click time, so newly-
  // sent messages wouldn't appear until a manual refresh.
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [draft, setDraft] = useState("");
  const [sendError, setSendError] = useState(null);
  const messagesEndRef = useRef(null);

  const selectedConversation = selectedConversationId
    ? conversations.find((c) => c.id === selectedConversationId) || null
    : null;

  const handleSend = async () => {
    if (!selectedConversation || !draft.trim() || sending) return;
    setSendError(null);
    const result = await onSendReply?.({
      conversationId: selectedConversation.id,
      body: draft,
    });
    if (result?.ok) {
      setDraft("");
    } else if (result?.error) {
      setSendError(result.error);
    }
  };

  useEffect(() => {
    if (typeof window.ahoy !== "undefined") {
      window.ahoy.track("succeedo_messages_viewed", { is_partner: !!isPartner });
    }
  }, [isPartner]);

  useEffect(() => {
    if (conversations.length === 0) return;
    if (selectedConversationId) return;
    setSelectedConversationId(conversations[0]?.id || null);
  }, [conversations, leadIdFromUrl, selectedConversationId]);

  // Scroll to bottom when the conversation changes OR a new message arrives.
  // Using id + message count avoids re-firing on every render (selectedConversation
  // is a find()-derived object whose identity changes each render).
  const messagesLength = selectedConversation?.messages?.length || 0;
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedConversationId, messagesLength]);

  // ActionCable: subscribe to the selected conversation's room and trigger
  // a refetch on every received message. The send_message mutation already
  // broadcasts to ChatRoom:#{tenant}-#{convo.id} from both sides, so this
  // closes the "the other party sent something but I have to refresh" gap.
  useEffect(() => {
    if (!selectedConversationId || !onIncomingMessage) return undefined;

    const consumer = createConsumer();
    const subscription = consumer.subscriptions.create(
      { channel: "ChatChannel", room_id: selectedConversationId },
      {
        received: (data) => {
          if (!data || data.type !== "message") return;
          onIncomingMessage();
        },
      },
    );

    return () => {
      subscription.unsubscribe();
      consumer.disconnect();
    };
  }, [selectedConversationId, onIncomingMessage]);

  if (loading) {
    return (
      <AppLayout
        breadcrumb={breadcrumb}
        persona={persona}
        userName={userName}
        userInitials={userInitials}
        isPartner={isPartner}
        planProgress={planProgress}
        actionPlanBadge={actionPlanBadge}
      >
        <div className="h-[calc(100vh-3.5rem)] flex items-center justify-center">
          <div className="text-[14px] text-muted-foreground">
            Loading conversations...
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      breadcrumb={breadcrumb}
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      isPartner={isPartner}
      planProgress={planProgress}
      actionPlanBadge={actionPlanBadge}
    >
      <div className="h-[calc(100dvh-3.5rem)] flex min-h-0 overflow-hidden">
        {/* Conversation List */}
        <div className="w-[300px] border-r border-border grid grid-rows-[auto_minmax(0,1fr)] min-h-0">
          <div className="p-4 border-b border-border">
            <h2 className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              CONVERSATIONS
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-4 text-[13px] text-muted-foreground text-center">
                No conversations yet
              </div>
            ) : (
              conversations.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => setSelectedConversationId(conversation.id)}
                  className={`w-full px-4 py-3 border-b border-border hover:bg-muted transition-colors text-left ${
                    selectedConversation?.id === conversation.id
                      ? "bg-muted border-l-2 border-[hsl(var(--succeedo-orange))]"
                      : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-muted text-foreground flex items-center justify-center font-mono text-[11px] font-medium shrink-0">
                      {conversation.other_party?.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[14px] font-medium text-foreground truncate">
                          {conversation.other_party?.name}
                        </div>
                        {conversation.unread && (
                          <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shrink-0" />
                        )}
                      </div>
                      <div className="text-[12px] text-muted-foreground truncate mb-1">
                        {conversation.last_message}
                      </div>
                      <div className="font-mono text-[10px] text-muted-foreground/40">
                        {formatTimestamp(conversation.last_message_time)}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Thread Area — grid with auto/1fr/auto rows is the most reliable
            way to keep the compose bar pinned to the bottom across iPad
            Safari, mobile, and desktop. flex-1 flex flex-col with min-h-0
            tends to break when an ancestor has padding the calc didn't
            account for. */}
        {selectedConversation ? (
          <div className="flex-1 grid grid-rows-[auto_minmax(0,1fr)_auto] min-h-0">
            {/* Thread Header — context block (label + link) is server-driven
                per conversation_type, so the same component renders advisory
                vs deal-room vs agent threads correctly. */}
            <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
              <div>
                <div className="text-[16px] font-medium text-foreground">
                  {selectedConversation.other_party?.name}
                </div>
                {selectedConversation.context?.label && (
                  <div className="text-[13px] text-muted-foreground">
                    {selectedConversation.context.label}
                  </div>
                )}
              </div>
              {selectedConversation.context?.link_url && (
                <a
                  href={selectedConversation.context.link_url}
                  className="inline-flex items-center gap-2 border border-border text-muted-foreground rounded-full px-3 py-1.5 text-[13px] hover:bg-muted transition-colors"
                >
                  <InfoIcon className="w-3.5 h-3.5" />
                  {selectedConversation.context.link_label || "View"}
                </a>
              )}
            </div>

            {/* Messages — min-h-0 inside grid-row 1fr lets the overflow
                scroll properly. */}
            <div className="overflow-y-auto p-6 space-y-4 min-h-0">
              {(selectedConversation.messages || []).length === 0 ? (
                <div className="text-[13px] text-muted-foreground text-center py-8">
                  No messages yet. Send the first one below.
                </div>
              ) : (
                (selectedConversation.messages || []).map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sent_by_me ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-[12px] p-3 ${
                        message.sent_by_me
                          ? "bg-[hsl(var(--succeedo-orange))]/10 ml-auto"
                          : "bg-muted"
                      }`}
                    >
                      <div className="text-[14px] text-foreground mb-1 whitespace-pre-line">
                        {message.body}
                      </div>
                      <div className="font-mono text-[10px] text-muted-foreground/40">
                        {formatTimestamp(message.sent_at)}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Compose Bar */}
            <div className="border-t border-border p-4 shrink-0 bg-card">
              {sendError && (
                <div className="mb-2 text-[12px] text-destructive">{sendError}</div>
              )}
              <div className="flex items-end gap-3">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Type your reply…  (⌘/Ctrl+Enter to send)"
                  disabled={sending}
                  className="flex-1 bg-muted border border-border rounded-[12px] px-4 py-3 text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20 resize-none min-h-[44px] max-h-[120px] disabled:opacity-50"
                  rows={2}
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!draft.trim() || sending}
                  className="bg-[hsl(var(--succeedo-orange))] text-white rounded-[8px] px-4 py-3 hover:bg-[#e65d0a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  aria-label="Send reply"
                >
                  <SendIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="text-[20px] font-light text-foreground mb-2">
                Select a conversation
              </div>
              <div className="text-[14px] text-muted-foreground">
                Choose a conversation from the list to start messaging
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
